import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';
import { alto, dustGray, java, LAYOUTS, Loader, mineShaft, thunderBird, white, wildSand } from '@parchment/atoms';
import { CancelIcon, UploadIcon } from '@parchment/icons';
import DocumentPreview from '@parchment/document-preview';
import { SCALE } from '../../utils/constants';
import FileCard from './FileCard';
import ImagePreview from './ImagePreview';
const ONE_MILLION = 1000000;
const FIVE_SECONDS = 5000;
const LOADING_SIZE = {
  small: '2em',
  medium: '3em',
  large: '5em'
};
const ROOT_SIZE = {
  small: '100px',
  medium: '130px',
  large: '250px'
};
const MESSAGE_FONT = {
  small: '16px',
  medium: '20px',
  large: '24px'
};
const OPTIONAL_MESSAGE_MARGIN = {
  small: '8px',
  medium: '10px',
  large: '20px'
};
const FILE_SIZE_FONT = {
  small: '12px',
  medium: '14px',
  large: '18px'
};
const FILE_SIZE_MARGIN = {
  small: '2px',
  medium: '5px',
  large: '10px'
};
const FILE_TYPE_FONT = {
  small: '8px',
  medium: '10px',
  large: '13px'
};
const FILE_TYPE_PADDING = {
  small: '0 6px',
  medium: '1px 8px',
  large: '2px 12px'
};
const DEFAULT_FILE_TYPES = ['DOC', 'DOCX', 'PNG', 'JPG', 'JPEG', 'PDF'];
const ICON_SIZE = {
  small: 'width: 30px; height: 30px;',
  medium: 'width: 35px; height: 35px;',
  large: 'width: 45px; height: 45px;'
};
const ICON_FONT = {
  small: '14px',
  medium: '16px',
  large: '20px'
};
export default function Dropzone(props) {
  const [fileTypes, setFileTypes] = useState(props.fileTypes && props.fileTypes.map(t => t.toUpperCase()) || DEFAULT_FILE_TYPES);
  const [maxFileSize, setMaxFileSize] = useState(props.maxSize || ONE_MILLION * 10);
  const [uploadMessage, setUploadMessage] = useState(props.uploadMessage || 'Upload A File');
  const [scale, setScale] = useState(props.scale || SCALE.medium);
  const [loading, setLoading] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(props.file);
  const [showPreview, setShowPreview] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  useEffect(() => setFileTypes(props.fileTypes && props.fileTypes.map(t => t.toUpperCase()) || DEFAULT_FILE_TYPES), [props.fileTypes]);
  useEffect(() => setMaxFileSize(props.maxSize || ONE_MILLION * 10), [props.maxSize]);
  useEffect(() => setUploadMessage(props.uploadMessage || 'Upload A File'), [props.uploadMessage]);
  useEffect(() => setScale(props.scale || SCALE.medium), [props.scale]);
  useEffect(() => setFileUploaded(props.file), [props.file]);
  const handleError = message => {
    setLoading(false);
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(undefined), FIVE_SECONDS);
  };
  const onDrop = useCallback(acceptedFiles => {
    if (acceptedFiles.length === 1) {
      const file = acceptedFiles[0];
      if (file.size <= maxFileSize) {
        const match = file.name.match(/.+\.(.+)/);
        if (props.enableFileExtensionMatch && match && match.length > 1 && fileTypes.includes(match[1].toUpperCase()) || !props.enableFileExtensionMatch) {
          setLoading(true);
          const reader = new FileReader();
          reader.onabort = () => handleError('ERROR: FILE UPLOAD INTERRUPTED');
          reader.onerror = () => handleError('ERROR: FILE NOT UPLOADED');
          reader.onload = () => {
            props.onLoadComplete(reader.result, file);
            setLoading(false);
            setFileUploaded(file);
          };
          reader.readAsArrayBuffer(file);
        } else {
          handleError('ERROR: WRONG FILE TYPE');
        }
      } else {
        handleError('ERROR: FILE SIZE LIMIT EXCEEDED');
      }
    }
  }, []);
  const onDropRejected = useCallback(rejectedFiles => {
    if (rejectedFiles.length >= 1) {
      const file = rejectedFiles[0];
      file.errors.length > 0 && handleError('ERROR: ' + file.errors[0].message);
    }
  }, []);
  const {
    getRootProps,
    getInputProps
  } = useDropzone({
    onDrop,
    onDropRejected,
    ...props
  });
  const handleCancelClick = e => {
    e && e.stopPropagation();
    props.onCancel && props.onCancel(e);
  };
  const handleDeleteClick = () => {
    if (props.onDelete) {
      props.onDelete(fileUploaded);
    } else {
      setFileUploaded(undefined);
    }
  };
  const handlePreviewClick = () => {
    props.onPreview && props.onPreview(fileUploaded);
    setShowPreview(true);
  };
  if (fileUploaded) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, fileUploaded.type === 'application/pdf' && /*#__PURE__*/React.createElement(DocumentPreview, {
      title: fileUploaded && fileUploaded.name,
      exitOnEscape: true,
      file: fileUploaded,
      show: showPreview,
      onExit: () => setShowPreview(false)
    }), fileUploaded.type.includes('image') && /*#__PURE__*/React.createElement(ImagePreview, {
      show: showPreview,
      onClose: () => setShowPreview(false),
      image: fileUploaded,
      isSVG: fileUploaded.type.includes('svg')
    }), /*#__PURE__*/React.createElement(FileCard, {
      fileName: fileUploaded.name,
      scale: scale,
      allowDelete: props.allowDelete,
      allowPreview: props.allowPreview,
      onDeleteClick: handleDeleteClick,
      onPreviewClick: handlePreviewClick
    }));
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Loader, {
    loading: loading,
    overlay: true,
    loadingMessage: "Uploading File",
    layout: LAYOUTS.VERTICAL,
    size: LOADING_SIZE[scale]
  }, /*#__PURE__*/React.createElement(Root, _extends({
    scale: scale
  }, getRootProps()), props.allowCancel && /*#__PURE__*/React.createElement(ButtonWrapper, {
    role: "button",
    scale: scale,
    onClick: handleCancelClick
  }, /*#__PURE__*/React.createElement(StyledCancelIcon, {
    color: java
  })), /*#__PURE__*/React.createElement("input", getInputProps()), /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement(Message, {
    scale: scale
  }, /*#__PURE__*/React.createElement(StyledUploadIcon, null), uploadMessage), /*#__PURE__*/React.createElement(FileSize, {
    scale: scale
  }, maxFileSize / ONE_MILLION + 'MB', " Max file size"), /*#__PURE__*/React.createElement(FileTypes, null, fileTypes.map((fileType, index) => {
    return /*#__PURE__*/React.createElement(FileType, {
      key: index,
      scale: scale
    }, fileType);
  })), props.optionalMessage && !errorMessage ? /*#__PURE__*/React.createElement(OptionalMessage, {
    scale: scale
  }, props.optionalMessage) : /*#__PURE__*/React.createElement(ErrorMessage, {
    scale: scale
  }, errorMessage)))));
}
const Root = /*#__PURE__*/styled.div.withConfig({
  displayName: "Dropzone__Root",
  componentId: "nd05ba-0"
})(["position:relative;min-height:", ";border:2px dashed ", ";padding:15px;display:flex;align-content:center;justify-content:center;&:active{background:", ";}"], props => ROOT_SIZE[props.scale || SCALE.medium], dustGray, wildSand);
const Container = /*#__PURE__*/styled.div.withConfig({
  displayName: "Dropzone__Container",
  componentId: "nd05ba-1"
})(["display:flex;flex-direction:column;align-self:center;"]);
const Message = /*#__PURE__*/styled.div.withConfig({
  displayName: "Dropzone__Message",
  componentId: "nd05ba-2"
})(["margin:auto;color:", ";text-align:center;font-weight:700;font-size:", ";"], java, props => MESSAGE_FONT[props.scale || SCALE.medium]);
const FileSize = /*#__PURE__*/styled.div.withConfig({
  displayName: "Dropzone__FileSize",
  componentId: "nd05ba-3"
})(["margin:", " auto;color:", ";font-weight:600;font-size:", ";"], props => FILE_SIZE_MARGIN[props.scale || SCALE.medium], dustGray, props => FILE_SIZE_FONT[props.scale || SCALE.medium]);
const FileTypes = /*#__PURE__*/styled.div.withConfig({
  displayName: "Dropzone__FileTypes",
  componentId: "nd05ba-4"
})(["margin:auto;"]);
const FileType = /*#__PURE__*/styled.div.withConfig({
  displayName: "Dropzone__FileType",
  componentId: "nd05ba-5"
})(["display:inline-block;color:", ";background-color:", ";padding:", ";border:1px solid ", ";border-radius:3px;font-weight:500;font-size:", ";margin:0 5px;"], mineShaft, wildSand, props => FILE_TYPE_PADDING[props.scale || SCALE.medium], alto, props => FILE_TYPE_FONT[props.scale || SCALE.medium]);
const StyledUploadIcon = /*#__PURE__*/styled(UploadIcon).withConfig({
  displayName: "Dropzone__StyledUploadIcon",
  componentId: "nd05ba-6"
})(["margin:0 5px;"]);
const ButtonWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "Dropzone__ButtonWrapper",
  componentId: "nd05ba-7"
})(["position:absolute;top:.25em;right:.5em;&:hover svg{color:", "}&:hover{background:", ";cursor:pointer;}", ";font-size:", ";border-radius:100%;"], white, java, props => ICON_SIZE[props.scale || SCALE.medium], props => ICON_FONT[props.scale || SCALE.medium]);
const StyledCancelIcon = /*#__PURE__*/styled(CancelIcon).withConfig({
  displayName: "Dropzone__StyledCancelIcon",
  componentId: "nd05ba-8"
})(["display:inline-block;top:50%;left:50%;transform:translate(-50%,-50%);"]);
const OptionalMessage = /*#__PURE__*/styled.div.withConfig({
  displayName: "Dropzone__OptionalMessage",
  componentId: "nd05ba-9"
})(["color:", ";font-size:", ";font-weight:500;text-align:center;margin-top:", ";"], mineShaft, props => FILE_SIZE_FONT[props.scale || SCALE.medium], props => OPTIONAL_MESSAGE_MARGIN[props.scale || SCALE.medium]);
const ErrorMessage = /*#__PURE__*/styled(OptionalMessage).withConfig({
  displayName: "Dropzone__ErrorMessage",
  componentId: "nd05ba-10"
})(["color:", ";"], thunderBird);